import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Shield, UserMinus, UserPlus } from "lucide-react";

import { sportsService, type Member, type Sport, type StudentOption, type Team } from "../../services/sportsService";
import { apiErrorMessage } from "../../utils/apiError";
import { todayIso } from "../../utils/dates";
import { Field, FormSection } from "../FormLayout";
import { FormModal } from "../FormModal";
import { Modal } from "../Modal";
import { ReasonPanel, errorLine } from "../inventory/InventoryParts";
import { SearchPick } from "../library/Pickers";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { ActionPanel, DetailsModal } from "../register/DetailsModal";
import { Notice, type NoticeState } from "../register/RecordParts";
import { POSITIONS, SPORT_TYPES, labelFrom, squadOrder, teamConfig } from "./sportsLogic";

function TeamForm({ team, sports, squad, onClose, onDone }: { team?: Team; sports: Sport[]; squad: Member[]; onClose: () => void; onDone: (row: Team) => void }) {
  const save = useMutation({
    mutationFn: (data: Record<string, unknown>) => (team ? sportsService.updateTeam({ id: team.id, data }) : sportsService.createTeam(data)),
    onSuccess: onDone,
  });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const text = (name: string) => String(form.get(name) ?? "").trim() || null;
    save.mutate({
      name: text("name"),
      ...(team ? { captain: text("captain") ? Number(text("captain")) : null, is_active: form.get("active") === "on" } : { sport: Number(text("sport")), is_active: true }),
      season: text("season"),
      year: Number(text("year")),
      team_motto: text("motto"),
      team_colors: text("colours"),
      description: text("description"),
    });
  };
  return (
    <FormModal
      id="team"
      title={team ? `Edit ${team.name}` : "New team"}
      busy={save.isPending}
      error={save.isError ? apiErrorMessage(save.error, "The team could not be saved.") : undefined}
      saveLabel={team ? "Save changes" : "Create the team"}
      note={team ? undefined : "You are recorded as the coach. Add players from the team."}
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="The team" divided={false}>
        <Field label="Name" hint="e.g. Under-14 football, Senior netball A.">
          <input required name="name" maxLength={200} defaultValue={team?.name} className="input mt-1" />
        </Field>
        {team ? (
          <Field label="Sport">
            <input disabled value={team.sport_name} className="input mt-1" />
          </Field>
        ) : (
          <Field label="Sport" hint={sports.some((row) => row.is_active) ? undefined : "Add the sport first, with Sports."}>
            <select required name="sport" className="input mt-1">
              <option value="">Choose a sport</option>
              {sports.filter((row) => row.is_active).map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
            </select>
          </Field>
        )}
        <Field label="Year">
          <input required type="number" name="year" min={2000} max={2100} defaultValue={team?.year ?? new Date().getFullYear()} className="input mt-1 tabular-nums" />
        </Field>
        <Field label="Season" optional hint="e.g. Term 2, 2026 league.">
          <input name="season" maxLength={100} defaultValue={team?.season ?? ""} className="input mt-1" />
        </Field>
        <Field label="Colours" optional>
          <input name="colours" maxLength={100} defaultValue={team?.team_colors ?? ""} className="input mt-1" />
        </Field>
        <Field label="Motto" optional>
          <input name="motto" maxLength={200} defaultValue={team?.team_motto ?? ""} className="input mt-1" />
        </Field>
        {team && (
          <Field label="Captain" optional>
            <select name="captain" defaultValue={team.captain ?? ""} className="input mt-1">
              <option value="">No captain</option>
              {squad.filter((row) => row.is_active).map((row) => <option key={row.student} value={row.student}>{row.student_name}</option>)}
            </select>
          </Field>
        )}
        {team && (
          <label className="flex items-center gap-2 self-end pb-2 text-sm font-medium text-gray-700">
            <input type="checkbox" name="active" defaultChecked={team.is_active} className="h-4 w-4 rounded border-gray-300" /> The team is active
          </label>
        )}
        <Field label="Description" optional className="sm:col-span-2">
          <textarea name="description" rows={2} defaultValue={team?.description ?? ""} className="input mt-1" />
        </Field>
      </FormSection>
    </FormModal>
  );
}

/** The sports the school plays; teams are made from these. */
function SportsModal({ sports, onClose, onChanged }: { sports: Sport[]; onClose: () => void; onChanged: () => void }) {
  const [form, setForm] = useState(0);
  const add = useMutation({
    mutationFn: sportsService.createSport,
    onSuccess: () => {
      setForm((count) => count + 1);
      onChanged();
    },
  });
  const toggle = useMutation({ mutationFn: (row: Sport) => sportsService.updateSport({ id: row.id, data: { is_active: !row.is_active } }), onSuccess: () => onChanged() });
  return (
    <Modal isOpen size="2xl" title="Sports" onClose={onClose} footer={<button type="button" className="btn btn-outline" onClick={onClose}>Close</button>}>
      <div className="space-y-5">
        {sports.length ? (
          <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
            {[...sports].sort((a, b) => a.name.localeCompare(b.name)).map((row) => (
              <li key={row.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
                <span>
                  <span className={`font-medium ${row.is_active ? "text-gray-900" : "text-gray-400"}`}>{row.name}</span>
                  <span className="ml-2 text-xs text-gray-500">{labelFrom(SPORT_TYPES, row.sport_type)} · {row.minimum_participants}–{row.maximum_participants} players{row.is_competitive ? " · competitive" : ""}</span>
                </span>
                <button type="button" disabled={toggle.isPending} onClick={() => toggle.mutate(row)} className="rounded px-2 py-0.5 text-xs font-medium text-primary-700 hover:bg-primary-50">
                  {row.is_active ? "Stop offering" : "Offer again"}
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="rounded-lg border border-dashed border-gray-300 py-6 text-center text-sm text-gray-500">No sports yet. Add the first below.</p>
        )}
        {toggle.isError && errorLine(toggle.error, "The sport could not be changed.")}
        <form
          key={form}
          onSubmit={(event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            const data = new FormData(event.currentTarget);
            add.mutate({
              name: String(data.get("name")).trim(),
              sport_type: String(data.get("sport_type")),
              minimum_participants: Number(data.get("min")),
              maximum_participants: Number(data.get("max")),
              is_competitive: data.get("competitive") === "on",
              is_active: true,
            });
          }}
          className="grid gap-3 rounded-lg border border-gray-200 p-4 sm:grid-cols-4 sm:items-end"
        >
          <Field label="New sport" className="sm:col-span-2">
            <input required name="name" maxLength={100} className="input mt-1" placeholder="e.g. Football, Netball, Athletics" />
          </Field>
          <Field label="Kind" className="sm:col-span-2">
            <select name="sport_type" defaultValue="team" className="input mt-1">
              {SPORT_TYPES.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
            </select>
          </Field>
          <Field label="Fewest players">
            <input required type="number" name="min" min={1} defaultValue={1} className="input mt-1 tabular-nums" />
          </Field>
          <Field label="Most players">
            <input required type="number" name="max" min={1} defaultValue={30} className="input mt-1 tabular-nums" />
          </Field>
          <label className="flex items-center gap-2 pb-2 text-sm text-gray-700">
            <input type="checkbox" name="competitive" defaultChecked className="h-4 w-4 rounded border-gray-300" /> Played competitively
          </label>
          <button disabled={add.isPending} className="btn btn-primary">Add</button>
          {add.isError && <div className="sm:col-span-4">{errorLine(add.error, "The sport could not be added.")}</div>}
        </form>
      </div>
    </Modal>
  );
}

/** Teams, their coach and captain, and the squad with each player's record. */
export function TeamsPanel({ teams, sports, learners, isLoading, isError, onRetry, canManage, isFamily, onChanged }: {
  teams?: Team[];
  sports: Sport[];
  learners: StudentOption[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  canManage: boolean;
  isFamily: boolean;
  onChanged: () => void;
}) {
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [editing, setEditing] = useState<Team | "new" | null>(null);
  const [sportsOpen, setSportsOpen] = useState(false);
  const [removing, setRemoving] = useState<Member | null>(null);
  const [learner, setLearner] = useState("");
  const [memberForm, setMemberForm] = useState(0);
  const squad = useQuery({ queryKey: ["sports", "squad", viewing], queryFn: () => sportsService.getTeamMembers(viewing!), enabled: viewing !== null });
  const refreshSquad = () => queryClient.invalidateQueries({ queryKey: ["sports", "squad", viewing] });
  const add = useMutation({
    mutationFn: sportsService.createMember,
    onSuccess: (row) => {
      setLearner("");
      setMemberForm((count) => count + 1);
      setNotice({ tone: "success", text: `${row.student_name} joined ${row.team_name}.` });
      refreshSquad();
      onChanged();
    },
  });
  const remove = useMutation({
    mutationFn: sportsService.removeMember,
    onSuccess: (row) => {
      setRemoving(null);
      setNotice({ tone: "success", text: `${row.student_name} left ${row.team_name}; their record is kept.` });
      refreshSquad();
      onChanged();
    },
  });

  const columns: RegisterColumn<Team>[] = [
    { header: "Team", cell: (row) => <span className="font-medium text-gray-900">{row.name}</span>, exportValue: (row) => row.name, className: "min-w-[12rem]" },
    { header: "Sport", cell: (row) => row.sport_name, exportValue: (row) => row.sport_name, className: "text-gray-700" },
    { header: "Coach", cell: (row) => row.coach_name, exportValue: (row) => row.coach_name, className: "text-gray-700" },
    { header: "Captain", cell: (row) => row.captain_name || "—", exportValue: (row) => row.captain_name ?? "", className: "text-gray-700" },
    { header: "Season", cell: (row) => [row.season, row.year].filter(Boolean).join(" · "), exportValue: (row) => [row.season, row.year].filter(Boolean).join(" "), className: "text-gray-700" },
    { header: "Status", cell: (row) => <StatusDot label={row.is_active ? "Active" : "Stood down"} dot={row.is_active ? "bg-emerald-500" : "bg-gray-400"} />, exportValue: (row) => (row.is_active ? "Active" : "Stood down") },
  ];

  const team = viewing !== null ? teams?.find((row) => row.id === viewing) : undefined;
  const members = squadOrder(squad.data ?? []);
  const inTeam = new Set(members.filter((row) => row.is_active).map((row) => row.student));
  const free = learners.filter((row) => !inTeam.has(row.id)).sort((a, b) => (a.class_level_order ?? 999) - (b.class_level_order ?? 999) || a.full_name.localeCompare(b.full_name));

  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        title="Teams"
        description={canManage ? "Open a team to see its squad, add or remove players, or edit it." : isFamily ? "The school's teams; open one to see your child's place in it." : "The school's teams and squads."}
        actions={canManage && (
          <>
            <button type="button" onClick={() => setSportsOpen(true)} className="btn btn-outline h-8 min-h-8 gap-1.5 px-3 text-xs">
              <Shield className="h-3.5 w-3.5" /> Sports
            </button>
            <button type="button" onClick={() => setEditing("new")} className="btn-primary h-8 min-h-8 px-3 text-xs">
              <UserPlus className="mr-1.5 h-3.5 w-3.5" /> New team
            </button>
          </>
        )}
        rows={teams}
        config={teamConfig}
        columns={columns}
        rowKey={(row) => row.id}
        onOpen={(row) => {
          add.reset();
          remove.reset();
          setRemoving(null);
          setViewing(row.id);
        }}
        noun={["team", "teams"]}
        exportName={{ file: "sports-teams", title: "Sports Teams" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText={canManage ? (sports.length ? "No teams yet. Create the first with New team." : "Start with Sports: add the sports the school plays, then its teams.") : "No teams yet."}
      />

      {team && !editing && (
        <DetailsModal
          size="3xl"
          title={team.name}
          headline={{ label: team.sport_name, value: `${members.filter((row) => row.is_active).length} ${isFamily ? "in the squad you can see" : "in the squad"}`, note: `Coach ${team.coach_name}${team.captain_name ? ` · captain ${team.captain_name}` : ""}` }}
          status={{ label: team.is_active ? "Active" : "Stood down", dot: team.is_active ? "bg-emerald-500" : "bg-gray-400" }}
          facts={[
            ["Season", [team.season, team.year].filter(Boolean).join(" · ")],
            ["Colours", team.team_colors ?? undefined],
            ["Motto", team.team_motto ?? undefined],
          ]}
          sections={[{ title: "About", body: team.description }]}
          onClose={() => setViewing(null)}
          footer={
            <>
              <button type="button" className="btn btn-outline" onClick={() => setViewing(null)}>Close</button>
              {canManage && (
                <button type="button" className="btn btn-primary gap-1.5" onClick={() => setEditing(team)}>
                  <Pencil className="h-4 w-4" /> Edit
                </button>
              )}
            </>
          }
        >
          <section>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Squad</h4>
            {squad.isLoading ? (
              <div className="mt-1.5 h-16 animate-pulse rounded bg-gray-100" />
            ) : members.length ? (
              <div className="sheet-wrap mt-1.5">
                <table className="sheet">
                  <thead>
                    <tr>
                      <th className="text-right">No.</th>
                      <th>Player</th>
                      <th>Position</th>
                      <th className="text-right">Played</th>
                      <th className="text-right">Goals</th>
                      <th className="text-right">Assists</th>
                      <th>Since</th>
                      {canManage && <th className="text-right">Action</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {members.map((row) => (
                      <tr key={row.id} className={row.is_active ? undefined : "opacity-50"}>
                        <td className="text-right tabular-nums">{row.jersey_number ?? "—"}</td>
                        <td className="font-medium text-gray-900">{row.student_name}{!row.is_active && <span className="ml-1 text-xs font-normal text-gray-500">(left {row.left_date})</span>}</td>
                        <td className="text-gray-700">{labelFrom(POSITIONS, row.position)}</td>
                        <td className="text-right tabular-nums">{row.games_played}</td>
                        <td className="text-right tabular-nums">{row.goals_scored}</td>
                        <td className="text-right tabular-nums">{row.assists}</td>
                        <td className="tabular-nums text-gray-700">{row.joined_date}</td>
                        {canManage && (
                          <td className="text-right">
                            {row.is_active && (
                              <button type="button" onClick={() => setRemoving(row)} className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium text-red-700 hover:bg-red-50">
                                <UserMinus className="h-3.5 w-3.5" /> Remove
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="mt-1.5 text-sm text-gray-500">{isFamily ? "Your child is not in this team." : "No players yet."}</p>
            )}
          </section>

          {canManage && removing && (
            <ReasonPanel
              title={`Remove ${removing.student_name}`}
              description="They leave the squad today; their games and goals stay on record."
              placeholder="e.g. Moved to the athletics squad."
              context={{ key: "sports.note", params: { kind: "member_remove", id: removing.id } }}
              button="Remove from the team"
              danger
              busy={remove.isPending}
              error={remove.error}
              errorText="The player could not be removed."
              onSubmit={(reason) => remove.mutate({ id: removing.id, reason })}
            />
          )}

          {canManage && team.is_active && (
            <ActionPanel title="Add a player" description="A learner who left before can be added back.">
              <form
                key={memberForm}
                onSubmit={(event: FormEvent<HTMLFormElement>) => {
                  event.preventDefault();
                  const data = new FormData(event.currentTarget);
                  add.mutate({
                    team: team.id,
                    student: Number(learner),
                    position: String(data.get("position")),
                    jersey_number: data.get("jersey") ? Number(data.get("jersey")) : null,
                    joined_date: String(data.get("joined")),
                  });
                }}
                className="grid gap-3 sm:grid-cols-3"
              >
                <Field label="Learner" as="div" className="sm:col-span-3">
                  <SearchPick
                    items={free}
                    value={learner}
                    onChange={setLearner}
                    label={(row) => row.full_name}
                    detail={(row) => [row.class_name, row.student_id].filter(Boolean).join(" · ")}
                    text={(row) => `${row.full_name} ${row.student_id}`}
                    placeholder="Search by name, class or learner number"
                    empty="No learner matches"
                    required
                  />
                </Field>
                <Field label="Position">
                  <select name="position" defaultValue="player" className="input mt-1">
                    {POSITIONS.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                  </select>
                </Field>
                <Field label="Shirt number" optional>
                  <input type="number" name="jersey" min={1} max={99} className="input mt-1 tabular-nums" />
                </Field>
                <Field label="Joined">
                  <input required type="date" name="joined" max={todayIso()} defaultValue={todayIso()} className="input mt-1" />
                </Field>
                <div className="flex justify-end sm:col-span-3">
                  <button disabled={add.isPending || !learner} className="btn btn-primary gap-1.5">
                    <UserPlus className="h-4 w-4" /> Add to the squad
                  </button>
                </div>
              </form>
              {add.isError && errorLine(add.error, "The player could not be added.")}
            </ActionPanel>
          )}
        </DetailsModal>
      )}

      {editing && (
        <TeamForm
          team={editing === "new" ? undefined : editing}
          sports={sports}
          squad={members}
          onClose={() => setEditing(null)}
          onDone={(saved) => {
            const created = editing === "new";
            setEditing(null);
            setNotice({ tone: "success", text: created ? `${saved.name} created. Add its players now.` : `${saved.name} updated.` });
            setViewing(saved.id);
            onChanged();
          }}
        />
      )}
      {sportsOpen && <SportsModal sports={sports} onClose={() => setSportsOpen(false)} onChanged={onChanged} />}
    </div>
  );
}
